import { useIdsSelect } from '@entities/common/IdsSelector/index';
import { useIdsSelecting } from '@entities/common/IdsSelector/models';
import { useSaveData } from '@entities/common/savedData/index.ts';
import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { areArraysWithNumbersEqual, doCheckArray } from '@shared/helpers/index.ts';
import { usePagesRedirect } from '@shared/hooks/use-pages-redirect.ts';
import type { FormContext } from '@shared/types/SharedFormsDefinitions';

const getInitData = (selectedGroupsIds: number[], initialValuesObject?: Record<string, unknown>) =>
  selectedGroupsIds && selectedGroupsIds.map((id) => ({ ...initialValuesObject, entityId: id }));

export const doCleanUnUsedEntityIds = (
  nameOfArray: string,
  formValues: Record<string, { entityId: number }[]>,
  doResetIds: (ids: number[]) => void
) => {
  let idsNeedToSave: number[] = [];

  formValues[nameOfArray] &&
    (idsNeedToSave = formValues[nameOfArray].reduce((newArr: number[], el) => {
      newArr.push(el.entityId);
      return newArr;
    }, []));

  doResetIds(idsNeedToSave);
};

/**
 * useFieldArrayCreatorConnectedToForm - название говорит само за себя
 *
 * @param nameOfArray имя массива в форме
 * @param nameOfEntity имя сущности для работы с idsSelector струкрутами
 * @param onRedirect clb функция, которая сработает первой при переходе на другую страницу
 * @param formContext контекст формы
 * @param redirectRoute куда редиректиться (список элементов определенной сущности)
 * @param mapCallBackFunctionForCreateStructure clb функция,
 * которая может иметь любую необходимую структуру и обязательное свойство id
 * Это св-во будет = id выбранной сущности
 *
 * @returns {formFields} - типичный formFields от useFeildArray (react-hook-form), с ноткой entityId от шефа
 * @returns {onAddEntityIds} - функция, которая осуществляет редирект на список элементов определенной сущности
 * @returns {register} - типичный register от useForm/useFormContext (react-hook-form)
 * @returns {setValue} - типичный setValue от useForm/useFormContext (react-hook-form)
 */
export type UseFieldArrayCreatorConnectedToFormT = Partial<FormContext> & {
  onRedirect?: () => void;
  redirectRoute: string;
  nameOfArray: string;
  nameOfEntity: string;
  initData?: unknown[];
  initialValuesObject?: Record<string, unknown>;
};

export const useFieldArrayCreatorConnectedToForm = ({
  nameOfEntity,
  nameOfArray,
  onRedirect,
  formContextFromProps,
  redirectRoute,
  initData,
  initialValuesObject,
}: UseFieldArrayCreatorConnectedToFormT) => {
  let formContext = useFormContext();
  const nameOfFieldArray = useMemo(() => `${nameOfArray}Editor`, [nameOfArray]);
  formContextFromProps && (formContext = formContextFromProps);

  const {
    register,
    setValue,
    getValues,
    control,
    formState: { errors: errors },
  } = formContext;

  const navigate = useNavigate();
  const { doRedirectWithSavePageUrl } = usePagesRedirect(nameOfEntity);
  const { setIsSelectingTrue } = useIdsSelecting(nameOfEntity);
  const { selectedIds, doResetSelectedIds, doRemoveId } = useIdsSelect(nameOfEntity);

  const {
    data: firstRender,
    doRemoveStorageData: removeFirstRenderData,
    doSetStorageData: setFirstRender,
  } = useSaveData<boolean>('groupCostEditorFirstRender');

  useLayoutEffect(() => {
    if (!selectedIds) {
      setFirstRender(true);
    }
  }, [selectedIds]);

  const {
    fields,
    append: appendFormFiled,
    remove: removeFormFiled,
    replace,
  } = useFieldArray({
    control,
    name: nameOfFieldArray,
  });
  // console.log('initData:',initData);
  useEffect(() => {
    const formData = getValues();
    // console.log('formData:', formData);
    // console.log('fields:', fields);
    // TS2339: Property entityId does not exist on type Record<'id', string> - ТАК НАДА!!!!!!!
    const fieldIdsFromFieldArray = fields.map((el) => el.entityId);
    const initDataIds = initData?.map((el) => el.entityId);

    // console.log('fieldIdsFromFieldArray:', fieldIdsFromFieldArray);

    const formFieldData = formData[nameOfFieldArray] as { entityId: number }[];
    // console.log('formFieldData:', formFieldData);
    if (selectedIds) {
      // console.log('SELECTEDIDS:', selectedIds);
      if (doCheckArray(formFieldData)) {
        const savedData: { entityId: number }[] = [];
        const newIds: number[] = [];
        const set = new Set<number>();

        // collect the data what we need to save
        // The new ids we collect in newIds
        if (firstRender) {
          // initData?.forEach((el) => {
          //   if (el.entityId) {
          //     // doesn't have in set
          //     !set.has(el.entityId) &&
          //       // selected
          //       selectedIds.includes(el.entityId) &&
          //       // add to saved
          //       savedData.push(el) &&
          //       // add to set
          //       set.add(el.entityId);
          //   }
          // });
          removeFirstRenderData();
          return;
        } else {
          formFieldData.forEach((el) => {
            if (el.entityId) {
              // doesn't have in set
              !set.has(el.entityId) &&
                // selected
                selectedIds.includes(el.entityId) &&
                // add to saved
                savedData.push(el) &&
                // add to set
                set.add(el.entityId);
            }
          });
        }
        // console.log('newIds:', newIds);
        // collecting all selected ids
        selectedIds.forEach(
          (id) => !set.has(id) && !newIds.includes(id) && newIds.push(id) && set.add(id)
        );

        // console.log('savedData:', savedData);
        // console.log('newIds:', newIds);
        const newData = [...savedData, ...getInitData(newIds, initialValuesObject)];
        // console.log('newData:', newData);
        // reset({ ...formData, [nameOfFieldArray]: newData });
        const newDataIds: number[] = [];
        set.forEach((el) => newDataIds.push(el));
        const areArraysEqual = areArraysWithNumbersEqual({
          firstArray: fieldIdsFromFieldArray,
          secondArray: newDataIds,
        });
        !areArraysEqual && replace(newData);
      } else {
        const areArraysEqual = areArraysWithNumbersEqual({
          firstArray: fieldIdsFromFieldArray,
          secondArray: selectedIds,
        });
        !areArraysEqual && replace(getInitData(selectedIds, initialValuesObject));
      }
    } else {
      // console.log('else');
      if (doCheckArray(initData)) {
        // console.log('initDataIds:', initDataIds);
        // TS18046: el is of type unknown - ТАК НАДА!!!!!!!
        const areArraysEqual = areArraysWithNumbersEqual({
          firstArray: fieldIdsFromFieldArray,
          secondArray: initDataIds,
        });
        // reset({ ...formData, [nameOfFieldArray]: initData });
        !areArraysEqual && replace(initData);
        doResetSelectedIds(initDataIds as number[]);
      }
    }
  }, [initData, replace, fields, getValues, selectedIds, nameOfFieldArray]);

  const onAddEntityIds = useCallback(() => {
    onRedirect && onRedirect();
    // doCleanUnUsedEntityIds(nameOfFieldArray, getValues(), doResetSelectedIds);
    setIsSelectingTrue();
    setTimeout(() => doRedirectWithSavePageUrl(redirectRoute), 5);
  }, [
    doRedirectWithSavePageUrl,
    // nameOfFieldArray,
    // getValues,
    setIsSelectingTrue,
    // doResetSelectedIds,
    onRedirect,
    redirectRoute,
  ]);

  const formFields = fields as unknown as { id: string; entityId: number }[];
  const formErrors = errors as unknown as Record<string, Array<any>>;

  return {
    doResetSelectedIds,
    formErrors,
    selectedIds,
    formFields,
    removeFormFiled,
    doRemoveId,
    appendFormFiled,
    onAddEntityIds,
    register,
    setValue,
    nameOfFieldArray,
  };
};
